// ─── App ────────────────────────────────────────────────────────────────────
export const APP_LABELS = {
  appName: 'Planly',
  back: 'Terug',
  moreActions: 'Meer acties',
  infoButton: 'Info',
  close: 'Sluiten',
  navDashboard: 'Dashboard',
  navPlanning: 'Planning',
  confirmDeleteTitle: 'Verwijderen bevestigen',
  confirmDeleteMessage: 'Ben je zeker dat je dit wilt verwijderen? Dit kan niet ongedaan worden gemaakt.',
  confirmDeleteCancel: 'Annuleren',
  confirmDeleteConfirm: 'Verwijderen',
} as const;

// ─── Auth ────────────────────────────────────────────────────────────────────
export const AUTH_LABELS = {
  accountDeactivated: 'Je account is gedeactiveerd. Neem contact op met je beheerder.',
  loadProfileFailed: 'Er is een fout opgetreden bij het laden van je profiel. Probeer opnieuw.',
} as const;

export const LOGIN_LABELS = {
  emailLabel: 'E-mailadres',
  emailPlaceholder: 'naam@bedrijf.be',
  passwordLabel: 'Wachtwoord',
  passwordPlaceholder: '••••••••',
  submitButton: 'Inloggen',
  magicLinkButton: 'Inloggen via e-maillink',
  dividerText: 'of',
  invalidCredentials: 'Ongeldige combinatie. Probeer opnieuw.',
  emailRequired: 'Vul eerst je e-mailadres in.',
  magicLinkSentTitle: 'Check je inbox',
  magicLinkSentBody: 'We stuurden een inloglink naar',
  dismissAlert: 'Melding sluiten',
  forgotPasswordLink: 'Wachtwoord vergeten?',
  forgotPasswordTitle: 'Wachtwoord resetten',
  forgotPasswordBody: 'Vul je e-mailadres in. We sturen je een link om je wachtwoord te resetten.',
  sendResetLink: 'Resetlink versturen',
  resetSentTitle: 'Check je inbox',
  resetSentBody: 'We stuurden een resetlink naar',
  backToLogin: 'Terug naar inloggen',
  resetError: 'Er is een fout opgetreden. Probeer opnieuw.',
} as const;

export const RESET_PASSWORD_LABELS = {
  welcome: 'Kies een nieuw wachtwoord.',
  passwordLabel: 'Nieuw wachtwoord',
  passwordHint: 'Minstens 8 tekens',
  confirmLabel: 'Bevestig wachtwoord',
  submitButton: 'Wachtwoord opslaan',
  passwordTooShort: 'Wachtwoord moet minstens 8 tekens bevatten.',
  passwordMismatch: 'Wachtwoorden komen niet overeen.',
  genericError: 'Er is een fout opgetreden. Probeer opnieuw.',
} as const;

export const SET_PASSWORD_LABELS = {
  welcome: 'Welkom! Stel je wachtwoord in om aan de slag te gaan.',
  passwordLabel: 'Nieuw wachtwoord',
  passwordHint: 'Minstens 8 tekens',
  confirmLabel: 'Bevestig wachtwoord',
  submitButton: 'Wachtwoord instellen',
  passwordTooShort: 'Wachtwoord moet minstens 8 tekens bevatten.',
  passwordMismatch: 'Wachtwoorden komen niet overeen.',
  genericError: 'Er is een fout opgetreden. Probeer opnieuw.',
} as const;

// ─── Planning ─────────────────────────────────────────────────────────────────
export const PLANNING_LABELS = {
noMachines: 'Geen machines voor dit project.',
  today: 'Vandaag',
  addMachine: 'Machine',
  filterTooltip: 'Filters',
  drawerTitle: 'Filters',
  prevWeek: 'Vorige week',
  nextWeek: 'Volgende week',
  monthsShort: ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'],
  projectLabel: 'Project',
  noProjects: 'Geen projecten',
  archivedSection: 'Gearchiveerd',
  reorderMode: 'Volgorde aanpassen',
  reorderModeExit: 'Klaar',
  reorderBanner: 'Versleep machines om de volgorde aan te passen.',
  reorderDragHandle: 'Versleep om te herordenen',
  resizeDragHandleStart: 'Sleep om startdatum aan te passen',
  resizeDragHandleEnd: 'Sleep om einddatum aan te passen',
  resizePendingAdd: 'Nieuwe periode — nog niet opgeslagen',
  resizePendingRemove: 'Wordt verwijderd bij loslaten',
  retryLoad: 'Opnieuw laden',
  selectDate: 'Selecteer datum',
  mutationError: 'Wijziging kon niet worden opgeslagen. Controleer uw internetverbinding.',
  machineAddSuccess: 'Machine toegevoegd.',
  machineUpdateSuccess: 'Machine bijgewerkt.',
  machineDeleteSuccess: 'Machine verwijderd.',
  bookingAddSuccess: 'Boeking toegevoegd.',
  bookingUpdateSuccess: 'Boeking bijgewerkt.',
  bookingDeleteSuccess: 'Boeking verwijderd.',
  bookingResizeSuccess: 'Boekingsperiode aangepast.',
  bookingResizeBlocked: 'Deze wijziging is niet mogelijk: buiten het beschikbaarheidsvenster van de machine of overlapt met een andere boeking.',
  reorderSuccess: 'Volgorde aangepast.',
  reorderError: 'Volgorde kon niet worden opgeslagen. Probeer opnieuw.',
  bulkActionSuccess: 'Bulkactie uitgevoerd.',
  bulkActionError: 'Bulkactie mislukt. Probeer opnieuw.',
  supplierAddSuccess: 'Leverancier toegevoegd.',
  categoryAddSuccess: 'Categorie toegevoegd.',
  optionAddSuccess: 'Optie toegevoegd.',
  addressAddSuccess: 'Adres toegevoegd.',
  subcontractorAddSuccess: 'Subcontractor toegevoegd.',
  subcontractorUserAddSuccess: 'Contactpersoon toegevoegd.',
  siteAddSuccess: 'Site / Werf toegevoegd.',
  pickupConfirmSuccess: 'Ophaling bevestigd.',
  handinConfirmSuccess: 'Teruggave bevestigd.',
} as const;

export const FILTER_LABELS = {
  searchPlaceholder: 'Zoeken...',
  availability: 'Beschikbaarheid',
  status: 'Bestelstatus',
  engineType: 'Motor type',
  category: 'Categorie',
  options: 'Opties / toebehoren',
  location: 'Locatie',
  sitesShowMore: (n: number) => `+ ${n} meer`,
  sitesShowLess: 'Minder tonen',
  machinesCount: (filtered: number, total: number) => `${filtered} van ${total} machines`,
  pickup: 'Ophaaldatum',
  pickupConfirmed: 'Bevestigd',
  pickupNotConfirmed: 'Niet bevestigd',
  filterActive: 'Filter actief',
  dashboardFilterArriving: 'Alle machines die vandaag/morgen starten.',
  dashboardFilterPickupConfirmed: 'Alle machines waarvan de ophaaldatum bevestigd is en vandaag of morgen valt.',
  dashboardFilterPickupAction: 'Alle machines waarvan de ophaaldatum nog niet is bevestigd en vandaag of morgen valt.',
  dashboardFilterToOrderAction: 'Alle nog te bestellen machines waarvan de startdatum vandaag of morgen valt.',
  dashboardFilterRentalExtension: 'De machines uit de Smart Planner+ suggestie.',
  dashboardFilterOverdueReturn: 'De boekingen die nog niet zijn teruggebracht.',
  dashboardFilterReset: 'Reset filters',
  supplier: 'Leverancier',
  subcontractor: 'Klant',
  saveFilters: 'Opslaan',
  exportButton: 'Exporteer',
} as const;

export const ENGINE_TYPE_LABELS = {
  none: 'Geen motor',
  electric: 'Elektrisch',
  diesel: 'Diesel',
  hybrid: 'Hybride',
} as const;

export const STATUS_LABELS = {
  to_order: 'Te bestellen',
  ordered: 'Besteld',
  collected: 'Opgehaald',
} as const;

export const AVAILABILITY_LABELS = {
  available: 'Beschikbaar',
  in_use: 'In gebruik',
  inactive: 'Inactief',
  reserved: 'Gereserveerd',
} as const;

export const BOOKING_PICKUP_LABELS = {
  pickupDialogTitle: 'Ophaling bevestigen',
  pickupDialogBody: 'Bevestig dat de machine wordt opgehaald voor deze boeking.',
  handinDialogTitle: 'Teruggave bevestigen',
  handinDialogBody: 'Bevestig dat de machine is teruggebracht voor deze boeking.',
  contactPersonLabel: 'Contactpersoon',
  contactPersonRequired: 'Contactpersoon is verplicht',
  actionConfirmPickup: 'Ophaling bevestigen',
  actionConfirmHandin: 'Teruggave bevestigen',
  cancelButton: 'Annuleren',
  saveButton: 'Bevestigen',
  statusReserved: 'Gereserveerd',
  statusInUse: 'In gebruik',
  statusReturned: 'Teruggebracht',
  overdueBadge: 'Nog niet teruggebracht',
  overdueBanner: 'Nog niet teruggebracht - verleng de boeking of bel de klant.',
  pickedUpByLabel: 'Opgehaald door',
  pickedUpAtLabel: 'Opgehaald op',
  handedInByLabel: 'Teruggebracht door',
  handedInAtLabel: 'Teruggebracht op',
  addUserOption: 'Nieuwe contactpersoon toevoegen',
  noUsers: 'Geen contactpersonen beschikbaar',
  blockedByBooking: (name: string) =>
    `Ophaling niet mogelijk: de machine is nog in gebruik voor de boeking van ${name}, die nog niet is teruggebracht.`,
  pickupNotPossible: 'Ophaling niet mogelijk',
} as const;

export const DETAIL_PANEL_LABELS = {
  close: 'Sluiten',
  machineNumber: 'Machinenummer',
  options: 'Opties / toebehoren',
  specifications: 'Specificaties',
  showMoreChips: (n: number) => `+${n} meer`,
  showLessChips: 'Toon minder',
  supplier: 'Leverancier',
  period: 'Periode',
  pickupOnEndDate: 'Ophaaldatum bevestigd',
  status: 'Status',
  engineType: 'Motor type',
  availability: 'Beschikbaarheid',
  moreInfo: 'Meer informatie',
  bookingsTitle: 'Boekingen',
  addBooking: 'Boeking',
  noBookings: 'Geen boekingen',
  groupActive: 'Actief',
  groupFuture: 'Gepland',
  groupPast: 'Afgelopen',
  noActiveBookings: 'Geen actieve boekingen',
  noFutureBookings: 'Geen geplande boekingen',
  noPastBookings: 'Geen afgelopen boekingen',
  deleteBookingTooltip: 'Boeking verwijderen',
  showAllBookings: 'Alle boekingen bekijken',
  moreBookings: (n: number) => `+${n} meer`,
  allBookingsTitle: 'Alle boekingen',
  editBookingTooltip: 'Boeking bewerken',
  // Actiemenu
  actionsMenu: 'Acties',
  actionStatus: 'Status updaten',
  actionPickupConfirm: 'Ophaaldatum bevestigen',
  actionPickupRemove: 'Ophaaldatum verwijderen',
  actionOrderEmail: 'Bestelling mailen',
  actionPickupConfirmMail: 'Afmelding mailen',
  actionMachineDetails: 'Machine bewerken',
  actionMachineNumber: 'Machinenummer bewerken',
  actionPeriod: 'Periode bewerken',
  actionToggleInactive: 'Inactief melden',
  actionReactivate: 'Reactiveren',
  actionDelete: 'Machine verwijderen',
  // Mail dialogen
  orderEmailDialogTitle: 'Mail versturen',
  orderEmailDialogBody: "Wil je ook de status aanpassen naar 'Besteld'?",
  emailDialogNo: 'Nee',
  emailDialogYes: 'Ja, aanpassen',
  pickupConfirmEmailDialogBody: 'Wil je de ophaaldatum ook bevestigen?',
  pickupRemoveDialogTitle: 'Ophaaldatum verwijderen',
  pickupRemoveDialogBody:
    'De ophaaldatum wordt verwijderd. Wil je de leverancier informeren via mail?',
  pickupRemoveConfirm: 'Verwijderen',
  pickupRemoveMail: 'Verwijderen en mailen',
  orderEmailSubject: 'Bestelling: {machine_category} {end_date} - {start_date}',
  orderEmailBody:
    'Beste,\n\nGraag bestellen wij onderstaande machine bij jullie:\n\nMachine: {machine_name}\nCategorie: {machine_category}\nMotor type: {engine_type}\nOpties/toebehoren: {options}\nProject: {project_name}\nLeveradres: {address}\n\nStartdatum: {start_date} {start_time}\nEinddatum: {end_date} {end_time}\nEinddatum definitief: {pickup_on_end_date}\n\nNotities: {notes}\n\nKunnen jullie deze bestelling even bevestigen? Alvast bedankt!\n\nMet vriendelijke groeten',
  returnEmailSubject: 'Afmelding: {machine_category} – {project_name}',
  returnEmailBody:
    'Beste,\n\nWij bevestigen de einddatum van onderstaande machine:\n\nMachine: {machine_name}\nMachinenummer: {machine_number}\nCategorie: {machine_category}\nMotor type: {engine_type}\nOpties/toebehoren: {options}\nProject: {project_name}\nLeveradres: {address}\n\nStartdatum: {start_date} {start_time}\nEinddatum: {end_date} {end_time}\nEinddatum definitief: {pickup_on_end_date}\n\nGelieve de machine op de vermelde einddatum op te halen. Contacteer ons voor de afspraken.\n\nMet vriendelijke groeten',
  mutationError: 'Wijziging kon niet worden opgeslagen. Controleer uw internetverbinding.',
  updateSuccess: 'Wijziging opgeslagen.',
  statusUpdateSuccess: 'Status bijgewerkt.',
  pickupConfirmSuccess: 'Ophaaldatum bevestigd.',
  pickupRemoveSuccess: 'Ophaaldatum verwijderd.',
  toggleInactiveSuccess: 'Machine inactief gemeld.',
  toggleActiveSuccess: 'Machine gereactiveerd.',
  machineDeleteSuccess: 'Machine verwijderd.',
  bookingDeleteSuccess: 'Boeking verwijderd.',
} as const;

export const DETAIL_ACTION_LABELS = {
  save: 'Opslaan',
  cancel: 'Annuleren',
  noSupplier: 'Geen leverancier',
  // Titels
  titleStatus: 'Status updaten',
  titleMachineDetails: 'Machine bewerken',
  titleMachineNumber: 'Machinenummer bewerken',
  titlePeriod: 'Periode bewerken',
  // Veldlabels
  statusLabel: 'Status',
  engineTypeLabel: 'Motor type',
  nameLabel: 'Naam',
  categoryLabel: 'Categorie',
  notesLabel: 'Notities',
  supplierLabel: 'Leverancier',
  machineNumberLabel: 'Machinenummer',
  optionsLabel: 'Opties / toebehoren',
  optionsPlaceholder: 'bv. rijplaten, verlegvorken, nummerplaat … (Enter om toe te voegen)',
  noLinkedOptions: 'Geen gekoppelde opties/toebehoren voor deze categorie.',
  noLinkedCategories: 'Geen categorie gekoppeld aan de geselecteerde optie(s).',
  startDateLabel: 'Startdatum',
  startTimeLabel: 'Startuur',
  endDateLabel: 'Einddatum',
  endTimeLabel: 'Einduur',
  // Validatie
  nameRequired: 'Naam is verplicht',
  specificationRequired: 'Dit veld is verplicht',
  startDateRequired: 'Startdatum is verplicht',
  startTimeRequired: 'Startuur is verplicht',
  endDateRequired: 'Einddatum is verplicht',
  endTimeRequired: 'Einduur is verplicht',
  endDateAfterStart: 'Einddatum moet na de startdatum liggen',
  endTimeAfterStart: 'Einduur moet na het startuur liggen',
  // Snel aanmaken
  addSupplierOption: 'Nieuwe leverancier toevoegen',
  addCategoryOption: 'Nieuwe categorie toevoegen',
  addOptionOption: 'Nieuwe optie toevoegen',
} as const;

export const BOOKING_DETAIL_LABELS = {
  status: 'Status',
  contact: 'Contactpersoon',
  site: 'Site / Werf',
  period: 'Periode',
  notes: 'Notities',
  edit: 'Bewerken',
  delete: 'Verwijderen',
  close: 'Sluiten',
  actionsMenu: 'Acties',
} as const;

export const MACHINE_FORM_PAGE_LABELS = {
  pageTitle: 'Machine toevoegen',
  backToPlanning: 'Terug naar planning',
  sectionMachineHeading: 'Machine',
  sectionLocationHeading: 'Locatie',
  sectionPlanningHeading: 'Planning',
  nameLabel: 'Naam',
  categoryLabel: 'Categorie',
  machineNumberLabel: 'Machinenummer',
  supplierLabel: 'Leverancier',
  statusLabel: 'Status',
  engineTypeLabel: 'Motor type',
  startDateLabel: 'Startdatum',
  startTimeLabel: 'Startuur',
  endDateLabel: 'Einddatum',
  endTimeLabel: 'Einduur',
  notesLabel: 'Notities',
  addressLabel: 'Adres',
  cancelButton: 'Annuleren',
  saveButton: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  categoryRequired: 'Categorie is verplicht',
  supplierRequired: 'Leverancier is verplicht',
  addressRequired: 'Adres is verplicht',
  noAddresses: 'Geen adressen beschikbaar - voeg eerst adressen toe aan het project.',
  startDateRequired: 'Startdatum is verplicht',
  endDateRequired: 'Einddatum is verplicht',
  endDateAfterStart: 'Einddatum moet na de startdatum liggen',
  endTimeAfterStart: 'Einduur moet na het startuur liggen',
  specificationRequired: 'Dit veld is verplicht',
  pickupOnEndDate: 'De einddatum is de ophaaldatum',
  optionsLabel: 'Opties / toebehoren',
  noLinkedOptions: 'Geen gekoppelde opties/toebehoren voor deze categorie.',
  noLinkedCategories: 'Geen categorie gekoppeld aan de geselecteerde optie(s).',
  templateLabel: 'Machinesjabloon (optioneel)',
  templatePlaceholder: 'Zoek op naam, bv. M30-bij-TVH',
  addSupplierOption: 'Nieuwe leverancier toevoegen',
  addCategoryOption: 'Nieuwe categorie toevoegen',
  addOptionOption: 'Nieuwe optie toevoegen',
  addAddressOption: 'Nieuw adres toevoegen',
} as const;

export const BOOKING_MODAL_LABELS = {
  addTitle: 'Boeking toevoegen',
  editTitle: 'Boeking bewerken',
  machineLabel: 'Machine',
  subcontractorLabel: 'Klant / Subcontractor',
  userLabel: 'Contactpersoon',
  siteLabel: 'Site / Werf',
  startDateLabel: 'Startdatum',
  startTimeLabel: 'Startuur',
  endDateLabel: 'Einddatum',
  endTimeLabel: 'Einduur',
  notesLabel: 'Notities',
  cancelButton: 'Annuleren',
  saveButton: 'Opslaan',
  machineRequired: 'Machine is verplicht',
  subcontractorRequired: 'Klant is verplicht',
  userRequired: 'Contactpersoon is verplicht',
  siteRequired: 'Site / Werf is verplicht',
  startDateRequired: 'Startdatum is verplicht',
  endDateRequired: 'Einddatum is verplicht',
  endDateAfterStart: 'Einddatum moet na de startdatum liggen',
  endTimeAfterStart: 'Einduur moet na het startuur liggen',
  bookingStartBeforeMachine: 'Startdatum ligt voor de startdatum van de machine',
  bookingStartTimeBeforeMachine: 'Startuur ligt voor het startuur van de machine',
  bookingEndAfterMachine: 'Einddatum ligt na de einddatum van de machine',
  bookingEndTimeAfterMachine: 'Einduur ligt na het einduur van de machine',
  bookingOverlapWarning: (subName: string) =>
    `Let op: deze boeking overlapt met een bestaande boeking voor ${subName} op deze machine.`,
  noSubcontractors: 'Geen klanten beschikbaar - maak eerst klanten aan in de instellingen.',
  noUsers: 'Geen contactpersonen beschikbaar voor deze klant.',
  addSubcontractorOption: 'Nieuwe subcontractor toevoegen',
  addUserOption: 'Nieuwe contactpersoon toevoegen',
  addSiteOption: 'Nieuwe site / werf toevoegen',
} as const;

// ─── Settings ─────────────────────────────────────────────────────────────────
// Shared across InviteUserModal, EditRoleModal, MemberRow and the admin member list.
export const ROLE_LABELS = {
  view_only: 'Bekijken',
  booking_only: 'Boekingen beheren',
  full_access: 'Volledige toegang',
} as const;

export const USERS_TAB_LABELS = {
  title: 'Gebruikers',
  subtitle: 'Beheer de leden van jouw organisatie.',
  inviteButton: 'Uitnodigen',
  tabActive: 'Actief',
  tabDeactivated: 'Gedeactiveerd',
  noActive: 'Geen actieve leden',
  noDeactivated: 'Geen gedeactiveerde leden',
  you: 'Jij',
  memberSince: 'Lid sinds',
  noEmail: '(geen e-mail)',
  deactivate: 'Deactiveren',
  reactivate: 'Reactiveren',
  editRole: 'Rol wijzigen',
  deactivateSuccess: 'Gebruiker gedeactiveerd.',
  reactivateSuccess: 'Gebruiker gereactiveerd.',
  roleUpdateSuccess: 'Rol bijgewerkt.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  lastFullAccessDeactivateBlocked: 'Dit is de enige actieve gebruiker met volledige toegang in deze organisatie en kan daarom niet gedeactiveerd worden. Geef eerst iemand anders volledige toegang.',
  lastFullAccessRoleBlocked: 'Dit is de enige actieve gebruiker met volledige toegang in deze organisatie, dus de rol kan niet gewijzigd worden. Geef eerst iemand anders volledige toegang.',
} as const;

export const EDIT_ROLE_MODAL_LABELS = {
  title: 'Rol wijzigen',
  roleLabel: 'Rol',
  cancel: 'Annuleren',
  save: 'Opslaan',
} as const;

export const INVITE_MODAL_LABELS = {
  title: 'Lid uitnodigen',
  firstNameLabel: 'Voornaam',
  lastNameLabel: 'Achternaam',
  emailLabel: 'E-mailadres',
  roleLabel: 'Rol',
  cancel: 'Annuleren',
  send: 'Uitnodiging verzenden',
  firstNameRequired: 'Voornaam is verplicht',
  lastNameRequired: 'Achternaam is verplicht',
  emailRequired: 'E-mailadres is verplicht',
  inviteSuccess: 'Uitnodiging verzonden.',
  inviteError: 'Uitnodiging mislukt. Controleer het e-mailadres en probeer opnieuw.',
} as const;

export const PROJECT_ROW_LABELS = {
  editProject: 'Bewerken',
  archiveProject: 'Archiveren',
  unarchiveProject: 'Dearchiveren',
  deleteProject: 'Verwijderen',
  viewHistory: 'Geschiedenis',
  addAddress: 'Adres toevoegen',
  editAddress: 'Adres bewerken',
  deleteAddress: 'Adres verwijderen',
  setDefaultAddress: 'Instellen als standaard adres',
  isDefaultAddress: 'Standaard adres',
  noAddresses: 'Nog geen adressen toegevoegd.',
} as const;

export const PROJECT_HISTORY_LABELS = {
  title: 'Geschiedenis',
  back: 'Terug naar projecten',
  noEntries: 'Nog geen wijzigingen geregistreerd voor dit project.',
  noResults: 'Geen wijzigingen gevonden voor deze filters.',
  loadMore: 'Meer laden',
  loadError: 'Er ging iets mis bij het laden van de geschiedenis.',
  retry: 'Opnieuw laden',
  entityTypeLabel: 'Type',
  entityAll: 'Alles',
  entityProject: 'Project',
  entityAddress: 'Adres',
  entityMachine: 'Machine',
  entityBooking: 'Boeking',
  actionLabel: 'Actie',
  actionAll: 'Alles',
  actionCreate: 'Aangemaakt',
  actionUpdate: 'Bijgewerkt',
  actionDelete: 'Verwijderd',
  actionArchive: 'Gearchiveerd',
  actionUnarchive: 'Gedearchiveerd',
  actionPickupConfirmed: 'Ophaling bevestigd',
  actionHandinConfirmed: 'Teruggave bevestigd',
  startDateLabel: 'Van',
  endDateLabel: 'Tot',
  searchLabel: 'Zoeken',
  searchPlaceholder: 'Zoek op naam...',
  resetFilters: 'Filters wissen',
  filterActive: 'Filter actief',
} as const;

export const ADDRESS_MODAL_LABELS = {
  createTitle: 'Nieuw adres',
  editTitle: 'Adres bewerken',
  nameLabel: 'Naam',
  streetLabel: 'Straatnaam',
  houseNumberLabel: 'Huisnummer',
  postalCodeLabel: 'Postcode',
  cityLabel: 'Gemeente',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  streetRequired: 'Straatnaam is verplicht',
  houseNumberRequired: 'Huisnummer is verplicht',
  postalCodeRequired: 'Postcode is verplicht',
  cityRequired: 'Gemeente is verplicht',
} as const;

export const PROJECTS_TAB_LABELS = {
  title: 'Projecten',
  subtitle: 'Beheer je projecten, pas ze aan of archiveer ze.',
  newProjectButton: 'Nieuw project',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve projecten',
  noArchived: 'Geen gearchiveerde projecten',
  deleteErrorHasMachines: 'Dit project heeft machines en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  limitExceededError: 'Maximum aantal actieve projecten bereikt voor deze organisatie.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Project opgeslagen.',
  deleteSuccess: 'Project verwijderd.',
  archiveSuccess: 'Project gearchiveerd.',
  unarchiveSuccess: 'Project gedearchiveerd.',
  addressSaveSuccess: 'Adres opgeslagen.',
  addressDeleteSuccess: 'Adres verwijderd.',
  addressDefaultSuccess: 'Standaardadres ingesteld.',
} as const;

export const PROJECT_MODAL_LABELS = {
  createTitle: 'Nieuw project',
  editTitle: 'Project bewerken',
  nameLabel: 'Naam',
  yearLabel: 'Jaar',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
} as const;

export const SITE_ROW_LABELS = {
  editSite: 'Bewerken',
  archiveSite: 'Archiveren',
  unarchiveSite: 'Dearchiveren',
  deleteSite: 'Verwijderen',
  allProjects: 'Alle projecten',
  unknownProject: 'Onbekend project',
} as const;

export const SITES_TAB_LABELS = {
  title: 'Sites / Werven',
  subtitle:
    'Beheer de sites en werven van je projecten. Je kunt een site verwijderen zolang er geen boekingen aan gekoppeld zijn. Zijn er wel boekingen aan gekoppeld, dan kun je de site archiveren, zodat deze niet meer in het dropdownmenu verschijnt bij het maken of bewerken van een boeking.',
  newSiteButton: 'Nieuwe site',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve sites',
  noArchived: 'Geen gearchiveerde sites',
  noProjects: 'Maak eerst een project aan om sites toe te voegen.',
  deleteErrorHasBookings: 'Deze site heeft boekingen en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Site opgeslagen.',
  deleteSuccess: 'Site verwijderd.',
  archiveSuccess: 'Site gearchiveerd.',
  unarchiveSuccess: 'Site gedearchiveerd.',
} as const;

export const SITE_MODAL_LABELS = {
  createTitle: 'Nieuwe site',
  editTitle: 'Site bewerken',
  nameLabel: 'Naam',
  scopeLabel: 'Project',
  scopeOrg: 'Alle projecten',
  colorLabel: 'Kleur',
  hexLabel: 'Hex kleur',
  hexError: 'Ongeldige hex kleur (bijv. #1e3a8a)',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  unknownProject: 'Onbekend project',
} as const;

export const SUBCONTRACTOR_ROW_LABELS = {
  edit: 'Klant bewerken',
  archive: 'Archiveren',
  unarchive: 'Dearchiveren',
  delete: 'Verwijderen',
  noUsers: 'Nog geen contactpersonen toegevoegd.',
  addUser: 'Contactpersoon toevoegen',
  setDefault: 'Instellen als standaard',
  isDefault: 'Standaard contactpersoon',
  editUser: 'Contactpersoon bewerken',
  deleteUser: 'Contactpersoon verwijderen',
} as const;

export const SUBCONTRACTORS_TAB_LABELS = {
  title: 'Klanten',
  subtitle:
    'Beheer de klanten en subcontractors van je organisatie. Je kunt een klant verwijderen zolang er geen boekingen aan gekoppeld zijn. Zijn er wel boekingen aan gekoppeld, dan kun je de klant archiveren.',
  newButton: 'Nieuwe klant',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve klanten',
  noArchived: 'Geen gearchiveerde klanten',
  deleteErrorHasBookings: 'Deze klant heeft boekingen en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Klant opgeslagen.',
  deleteSuccess: 'Klant verwijderd.',
  archiveSuccess: 'Klant gearchiveerd.',
  unarchiveSuccess: 'Klant gedearchiveerd.',
  userSaveSuccess: 'Contactpersoon opgeslagen.',
  userDeleteSuccess: 'Contactpersoon verwijderd.',
  userDefaultSuccess: 'Standaard contactpersoon ingesteld.',
} as const;

export const SUBCONTRACTOR_MODAL_LABELS = {
  createTitle: 'Nieuwe klant',
  editTitle: 'Klant bewerken',
  nameLabel: 'Naam',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
} as const;

export const USER_MODAL_LABELS = {
  createTitle: 'Nieuwe contactpersoon',
  editTitle: 'Contactpersoon bewerken',
  firstNameLabel: 'Voornaam',
  lastNameLabel: 'Achternaam',
  cancel: 'Annuleren',
  save: 'Opslaan',
  firstNameRequired: 'Voornaam is verplicht',
  lastNameRequired: 'Achternaam is verplicht',
} as const;

export const SETTINGS_TAB_LABELS = {
  bookingTimesTitle: 'Standaard boekingstijden',
  bookingTimesSubtitle: 'Worden automatisch ingevuld bij het aanmaken van een nieuwe boeking.',
  bookingTimesModalTitle: 'Boekingstijden bewerken',
  startTimeLabel: 'Standaard startuur',
  endTimeLabel: 'Standaard einduur',
  machineSettingsTitle: 'Machine-instellingen',
  machineSettingsSubtitle: 'Worden automatisch ingevuld bij het aanmaken van een nieuwe machine.',
  machineSettingsModalTitle: 'Machine-instellingen bewerken',
  machineTimesTitle: 'Standaard machine-uren',
  machineStartTimeLabel: 'Standaard startuur machine',
  machineEndTimeLabel: 'Standaard einduur machine',
  machineEndDateDefinitiveLabel: 'Ophaaldatum standaard bevestigen',
  machineEndDateDefinitiveDesc: 'De ophaaldatum wordt standaard als bevestigd gemarkeerd bij het aanmaken van een nieuwe machine.',
  machineDefaultOrderedLabel: 'Status standaard "Besteld"',
  machineDefaultOrderedDesc: 'De status van een nieuwe machine is standaard "Besteld" in plaats van "Te bestellen".',
  orderEmailTemplateTitle: 'Bestelmail',
  orderEmailTemplateSubtitle: 'Het sjabloon dat gebruikt wordt bij "Bestellen bij leverancier".',
  orderEmailTemplateModalTitle: 'Bestelmail aanpassen',
  orderEmailSubjectLabel: 'Onderwerp',
  orderEmailBodyLabel: 'Inhoud',
  orderEmailVariablesInfo:
    'Beschikbare variabelen: {machine_name}, {machine_category}, {engine_type}, {options}, {project_name}, {address}, {start_date}, {start_time}, {end_date}, {end_time}, {pickup_on_end_date}, {notes}',
  orderEmailResetButton: 'Standaard herstellen',
  orderEmailCustomBadge: 'Aangepast sjabloon',
  orderEmailDefaultBadge: 'Standaard sjabloon',
  returnEmailTemplateTitle: 'Afmeldmail',
  returnEmailTemplateSubtitle: 'Het sjabloon dat gebruikt wordt bij "Afmelding mailen".',
  returnEmailTemplateModalTitle: 'Afmeldmail aanpassen',
  returnEmailSubjectLabel: 'Onderwerp',
  returnEmailBodyLabel: 'Inhoud',
  returnEmailVariablesInfo:
    'Beschikbare variabelen: {machine_name}, {machine_number}, {machine_category}, {engine_type}, {options}, {project_name}, {address}, {start_date}, {start_time}, {end_date}, {end_time}, {pickup_on_end_date}',
  returnEmailResetButton: 'Standaard herstellen',
  returnEmailCustomBadge: 'Aangepast sjabloon',
  returnEmailDefaultBadge: 'Standaard sjabloon',
  editButton: 'Bewerken',
  cancel: 'Annuleren',
  saveButton: 'Opslaan',
  saveFailed: 'Opslaan mislukt. Probeer opnieuw.',
  bookingTimesSaveSuccess: 'Boekingstijden opgeslagen.',
  machineSettingsSaveSuccess: 'Machine-instellingen opgeslagen.',
  orderEmailSaveSuccess: 'Bestelmail opgeslagen.',
  returnEmailSaveSuccess: 'Afmeldmail opgeslagen.',
} as const;

// ─── Dashboard ───────────────────────────────────────────────────────────────
export const DASHBOARD_LABELS = {
  title: 'Dashboard',
  noProject: 'Selecteer een project via het instellingenmenu om het dashboard te laden.',
  cardArriving: 'Aankomend',
  cardPickup: 'Vertrekkend',
  cardAction: 'Actie vereist',
  cardToOrder: 'Te bestellen',
  overdueReturnSummary: (count: number) => `${count} ${count === 1 ? 'boeking' : 'boekingen'} te laat`,
  overdueReturnAllOnTime: 'Alles op tijd teruggebracht',
  overdueReturnDays: (n: number) => `${n} ${n === 1 ? 'dag' : 'dagen'}`,
  overdueReturnHours: (n: number) => `${n} uur`,
  overdueReturnMore: (n: number) => `+${n} meer`,
  labelToday: 'Vandaag',
  labelTomorrow: 'Morgen',
  linkToPlanning: 'Bekijk in planning',
  machineStatusTitle: 'Machines per status',
  machineAvailabilityTitle: 'Machines per beschikbaarheid',
  machineEngineTypeTitle: 'Machines per motor type',
  machineCategoryTitle: 'Machines per categorie',
  categoryOther: (n: number) => `Overige (${n})`,
  siteOccupancyTitle: 'Machines per site',
  subcontractorTitle: 'Boekingen per klant',
  noData: 'Geen data beschikbaar voor dit project.',
  days: 'dagen',
  bookings: 'Boekingen',
  machines: 'Machines',
  downloadBookingsLabel: 'Exporteer boekingen per klant',
  infoArriving:
    'Machines die vandaag of morgen op de werf worden verwacht. Dit zijn machines waarvan de startdatum vandaag of morgen valt.',
  infoPickup:
    'Machines die vandaag of morgen door de leverancier worden opgehaald. Dit zijn machines waarvan de ophaaldatum bevestigd is en vandaag of morgen valt.',
  infoAction:
    'Machines waarvan de einddatum vandaag of morgen valt, maar waarvoor de ophaaldatum bij de leverancier nog niet bevestigd is.\n\nOpen de machine in de planning om de ophaaldatum te bevestigen.',
  infoToOrder:
    'Machines met status "Te bestellen" waarvan de startdatum vandaag of morgen valt, maar die nog niet besteld zijn bij een leverancier.\n\nOpen de machine in de planning om de bestelling te plaatsen.',
  timelineTitle: 'Boekingen tijdlijn - volgende 14 dagen',
  timelineArrivals: 'Aankomsten',
  timelineReturns: 'Retours',
  importButton: 'Importeer',
  importDialogTitle: 'Importeer machines',
  importTemplateAction: 'Download template',
  importTemplateDesc:
    'Download een Excel-template voor dit project. Het bestand bevat alvast de beschikbare leveranciers en adressen.',
  importUploadAction: 'Importeer xlsx',
  importUploadDesc:
    'Upload een ingevuld Excel-bestand om machines in bulk toe te voegen aan het geselecteerde project.',
  extensionTitle: 'Smart Planner+',
  infoExtension: `Smart Planner+ kijkt voor elke machine met status 'Te bestellen' of er een andere machine van dezelfde categorie én hetzelfde leveradres is waarvan de periode (bijna) aansluit - die bestelling is nog niet geplaatst en kan dus nog omgeleid worden. Gebruik die andere machine in plaats van een nieuwe te bestellen: dat bespaart transporten en levert vaak een goedkopere dagprijs op bij een langere huurtermijn. Een 'Te bestellen' machine kan meerdere alternatieven hebben.\n\nDe marge (in dagen) die je instelt bepaalt niet alleen hoe ver, maar ook in welke richting gezocht wordt. Vul je een positief getal in (bijvoorbeeld 2), dan tonen we enkel machines die tot dat aantal dagen na de gewenste periode vrijkomen - zonder overlap, meteen bruikbaar. Vul je een negatief getal in (bijvoorbeeld -2), dan tonen we net het omgekeerde: enkel machines waarvan de periode tot dat aantal dagen overlapt met de gewenste periode - de betrokken data moeten dan nog aangepast worden om de combinatie te laten passen. Vul je 0 in, dan tonen we uitsluitend machines die exact naadloos aansluiten. Standaard staat de marge op 2 dagen. Wil je net overlappende machines opsporen, zet de marge dan zelf op een negatief getal.\n\nReeds bestelde machines tellen mee als alternatief, ongeacht of hun ophaaldatum al bevestigd is - zolang de ophaaldatum niet bevestigd is, staat de machine immers nog bij de klant, ook al is de geplande einddatum al voorbij. Enkel wanneer de ophaaldatum wél bevestigd is én de einddatum al voorbij is, wordt de machine uitgesloten: die is dan effectief niet meer beschikbaar.\n\nOntbrekende opties/toebehoren van een alternatief ten opzichte van de te vervangen machine worden altijd getoond. Standaard houden we hier verder geen rekening mee: een alternatief dat opties mist, wordt gewoon als suggestie getoond. Schakel 'Houd rekening met opties' zelf in om enkel nog alternatieven te tonen die volledig matchen: zodra er ook maar één optie of toebehoren ontbreekt, wordt dat alternatief dan niet meer voorgesteld. Gebruik de instellingenknop om dit en de marge aan te passen.`,
  extensionPeriod: (start: string, end: string) => `${start} – ${end}`,
  extensionStarting: 'Start op',
  extensionEnding: 'Eindigt op',
  extensionGapSeamless: 'Aansluitend',
  extensionGapMargin: (days: number) => `${days} ${days === 1 ? 'dag' : 'dagen'} marge`,
  extensionGapOverlap: (days: number) => `${days} ${days === 1 ? 'dag' : 'dagen'} overlap`,
  extensionFullMatch: 'Volledige match',
  extensionMissingOptions: (options: string[]) => `Mist: ${options.join(', ')}`,
  extensionConsiderOptionsLabel: 'Houd rekening met opties',
  extensionSummary: (count: number) =>
    `${count} ${count === 1 ? 'verlengsuggestie' : 'verlengsuggesties'} gevonden`,
  extensionViewAll: 'Bekijk alle suggesties',
  extensionDialogTitle: 'Smart Planner+ suggesties',
  extensionSuggestionCounter: (index: number, total: number) => `Suggestie ${index} van ${total}`,
  extensionPrevious: 'Vorige',
  extensionNext: 'Volgende',
  extensionNotInterested: 'Geen interesse',
  extensionViewInPlanning: 'Bekijken in planning',
  extensionAllDismissed: 'Je hebt alle suggesties bekeken.',
  extensionCurrentSuggestion: 'Asset te vervangen',
  extensionAlternatives: 'Verlengingsalternatieven',
  extensionWindowDaysLabel: 'Marge (dagen)',
  extensionWindowDaysHelp:
    'Positief getal: toon enkel machines die tot dit aantal dagen na de gewenste periode vrijkomen (geen overlap). Negatief getal: toon enkel machines waarvan de periode tot dit aantal dagen overlapt. 0: toon enkel een naadloze aansluiting.',
  extensionEmpty: 'Geen suggesties gevonden binnen deze marge.',
  extensionConsiderOptionsHelp:
    'Een voorgesteld alternatief dat ook maar één optie of toebehoren mist, wordt dan niet meer voorgesteld.',
  extensionSettingsButton: 'Instellingen Smart Planner+',
  extensionSettingsTitle: 'Instellingen Smart Planner+',
  extensionSettingsDescription:
    'Deze instellingen bepalen welke machines als verlengsuggestie worden getoond. Ze worden onthouden op je account, en gelden dus op elk toestel waarop je inlogt.',
  extensionResetDismissed: 'Alles opnieuw tonen',
  extensionResetDismissedDescription:
    'Haal alle suggesties terug waarvoor je eerder op \'Geen interesse\' hebt geklikt.',
  save: 'Opslaan',
  cancel: 'Annuleren',
  extensionResetSuccess: 'Suggesties worden opnieuw getoond.',
  extensionResetError: 'Er is een fout opgetreden. Probeer opnieuw.',
  extensionSaveSuccess: 'Instellingen opgeslagen.',
  extensionSaveError: 'Er is een fout opgetreden. Probeer opnieuw.',
  extensionDismissSuccess: 'Suggestie verborgen.',
  extensionDismissError: 'Er is een fout opgetreden. Probeer opnieuw.',
} as const;

// ─── Pages ────────────────────────────────────────────────────────────────────
export const SETTINGS_PAGE_LABELS = {
  pageGeneral: 'Instellingen',
} as const;

export const SETTINGS_NAV_LABELS = {
  menuButton: 'Instellingen',
  users: 'Gebruikers',
  projects: 'Projecten',
  sites: 'Sites',
  subcontractors: 'Subcontractors',
  suppliers: 'Leveranciers',
  machineData: 'Machinegegevens',
  general: 'Instellingen',
  themeLight: 'Licht thema',
  themeDark: 'Donker thema',
  adminTooltip: 'Admin',
  userMenuButton: 'Jouw account',
  account: 'Account',
  signOut: 'Uitloggen',
  planning: 'Planning',
  mobileMenuOpen: 'Menu openen',
  mobileMenuClose: 'Menu sluiten',
  mobileMenuTitle: 'Navigatiemenu',
  help: 'Help',
} as const;

// ─── Suppliers ────────────────────────────────────────────────────────────────
export const SUPPLIER_ROW_LABELS = {
  edit: 'Bewerken',
  archive: 'Archiveren',
  unarchive: 'Dearchiveren',
  delete: 'Verwijderen',
} as const;

export const SUPPLIERS_TAB_LABELS = {
  title: 'Leveranciers',
  subtitle: 'Beheer de leveranciers van je machines.',
  newButton: 'Nieuwe leverancier',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve leveranciers.',
  noArchived: 'Geen gearchiveerde leveranciers.',
  deleteErrorHasMachines: 'Deze leverancier heeft machines en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Leverancier opgeslagen.',
  deleteSuccess: 'Leverancier verwijderd.',
  archiveSuccess: 'Leverancier gearchiveerd.',
  unarchiveSuccess: 'Leverancier gedearchiveerd.',
} as const;

export const SUPPLIER_MODAL_LABELS = {
  createTitle: 'Nieuwe leverancier',
  editTitle: 'Leverancier bewerken',
  nameLabel: 'Naam',
  emailLabel: 'E-mailadres',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
} as const;

// ─── Machinegegevens ──────────────────────────────────────────────────────────
export const MACHINE_DATA_PAGE_LABELS = {
  title: 'Machinegegevens',
  tabCategories: 'Categorieën',
  tabOptions: 'Opties / Toebehoren',
  tabTemplates: 'Machinesjablonen',
} as const;

export const CATEGORY_ROW_LABELS = {
  edit: 'Bewerken',
  archive: 'Archiveren',
  unarchive: 'Dearchiveren',
  delete: 'Verwijderen',
  showMoreOptions: (n: number) => `+${n} meer`,
  showLessOptions: 'Toon minder',
  noOptionsLinked: 'Geen opties gekoppeld',
} as const;

export const CATEGORIES_TAB_LABELS = {
  title: 'Categorieën',
  subtitle: 'Beheer de categorieën waaruit gekozen kan worden bij het aanmaken van een machine.',
  newButton: 'Nieuwe categorie',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve categorieën.',
  noArchived: 'Geen gearchiveerde categorieën.',
  deleteErrorHasMachines: 'Deze categorie is nog in gebruik (bij machines of een vooraf gedefinieerde machine) en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Categorie opgeslagen.',
  deleteSuccess: 'Categorie verwijderd.',
  archiveSuccess: 'Categorie gearchiveerd.',
  unarchiveSuccess: 'Categorie gedearchiveerd.',
} as const;

export const CATEGORY_MODAL_LABELS = {
  createTitle: 'Nieuwe categorie',
  editTitle: 'Categorie bewerken',
  nameLabel: 'Naam',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  nameDuplicate: 'Er bestaat al een categorie met deze naam.',
  optionsHeading: 'Gekoppelde opties/toebehoren',
  optionsInfo: 'Kies welke opties/toebehoren van toepassing zijn voor deze categorie.',
  optionSearchPlaceholder: 'Optie zoeken...',
  addOptionButton: 'Nieuwe optie toevoegen',
  noOptionsFound: 'Geen opties gevonden.',
  specificationsHeading: 'Specificaties',
  specificationsInfo: 'Bepaal welke specificaties verplicht of vrij zijn bij het aanmaken van een machine van deze categorie.',
  specificationLabel: 'Specificatie',
  specificationPlaceholder: 'Kies een specificatie...',
  specificationRequired: 'Kies een specificatie of verwijder deze rij.',
  specRequiredLabel: 'Verplicht',
  removeSpecification: 'Specificatie verwijderen',
  addSpecificationButton: 'Voeg specificatie toe',
} as const;

export const OPTION_ROW_LABELS = {
  edit: 'Bewerken',
  archive: 'Archiveren',
  unarchive: 'Dearchiveren',
  delete: 'Verwijderen',
} as const;

export const OPTIONS_TAB_LABELS = {
  title: 'Opties / Toebehoren',
  subtitle: 'Beheer de opties/toebehoren waaruit gekozen kan worden bij het aanmaken van een machine.',
  newButton: 'Nieuwe optie',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve opties.',
  noArchived: 'Geen gearchiveerde opties.',
  deleteErrorHasMachines: 'Deze optie is nog in gebruik en kan niet verwijderd worden.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Optie opgeslagen.',
  deleteSuccess: 'Optie verwijderd.',
  archiveSuccess: 'Optie gearchiveerd.',
  unarchiveSuccess: 'Optie gedearchiveerd.',
} as const;

export const OPTION_MODAL_LABELS = {
  createTitle: 'Nieuwe optie',
  editTitle: 'Optie bewerken',
  nameLabel: 'Naam',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  nameDuplicate: 'Er bestaat al een optie met deze naam.',
  addedToListHint: 'Nieuwe opties die je hier aanmaakt, worden automatisch toegevoegd aan de lijst "Opties/toebehoren".',
  categoriesHeading: 'Gekoppelde categorieën',
  categoriesInfo: 'Kies aan welke categorieën deze optie/toebehoren beschikbaar is.',
  categorySearchPlaceholder: 'Categorie zoeken...',
  noCategoriesFound: 'Geen categorieën gevonden.',
} as const;

export const MACHINE_TEMPLATE_ROW_LABELS = {
  edit: 'Bewerken',
  archive: 'Archiveren',
  unarchive: 'Dearchiveren',
  delete: 'Verwijderen',
} as const;

export const MACHINE_TEMPLATES_TAB_LABELS = {
  title: 'Machinesjablonen',
  subtitle: 'Maak sjablonen die categorie, motortype, leverancier en opties automatisch invullen bij het aanmaken van een nieuwe machine.',
  newButton: 'Nieuw sjabloon',
  tabActive: 'Actief',
  tabArchived: 'Gearchiveerd',
  noActive: 'Geen actieve sjablonen.',
  noArchived: 'Geen gearchiveerde sjablonen.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  dismissError: 'Melding sluiten',
  saveSuccess: 'Sjabloon opgeslagen.',
  deleteSuccess: 'Sjabloon verwijderd.',
  archiveSuccess: 'Sjabloon gearchiveerd.',
  unarchiveSuccess: 'Sjabloon gedearchiveerd.',
} as const;

export const MACHINE_TEMPLATE_MODAL_LABELS = {
  createTitle: 'Nieuw sjabloon',
  editTitle: 'Sjabloon bewerken',
  nameLabel: 'Naam',
  namePlaceholder: 'bv. M30-D',
  categoryLabel: 'Categorie',
  engineTypeLabel: 'Motor type',
  supplierLabel: 'Leverancier',
  optionsLabel: 'Opties / toebehoren',
  noLinkedOptions: 'Geen gekoppelde opties/toebehoren voor deze categorie.',
  noOptionsFound: 'Geen opties gevonden.',
  cancel: 'Annuleren',
  save: 'Opslaan',
  nameRequired: 'Naam is verplicht',
  categoryRequired: 'Categorie is verplicht',
  specificationRequired: 'Dit veld is verplicht',
} as const;

// Dutch titles for the fixed SpecificationKey catalog (src/features/planning/types.ts) — unit
// and decimal precision live alongside the keys themselves in SPECIFICATION_DEFINITIONS
// (src/features/planning/constants.ts), since those aren't user-facing text.
export const SPECIFICATION_LABELS = {
  max_lift_capacity: 'Max heftcapaciteit',
  drive_through_height: 'Doorrijhoogte',
  drive_through_width: 'Doorrijbreedte',
  max_working_reach: 'Max werkreikwijdte',
  weight: 'Gewicht',
  length: 'Lengte',
  width: 'Breedte',
  max_length_per_stamp: 'Max. lengte per stempel',
  lift_capacity: 'Hefcapaciteit',
  person_count: 'Aantal personen',
  loading_bed_capacity: 'Laadbak-capaciteit',
  mast_drive_through_height: 'Doorrijhoogte mast',
} as const;

export const ACCOUNT_PAGE_LABELS = {
  sectionTitle: 'Account',
  sectionSubtitle: 'Jouw accountinformatie en persoonlijke gegevens.',
  memberSince: 'Lid sinds',
  editProfile: 'Profiel bewerken'
} as const;

export const EDIT_PROFILE_MODAL_LABELS = {
  title: 'Profiel bewerken',
  firstNameLabel: 'Voornaam',
  lastNameLabel: 'Achternaam',
  emailLabel: 'E-mailadres',
  save: 'Opslaan',
  cancel: 'Annuleren',
  close: 'Sluiten',
  emailSentMsg: (email: string) =>
    `Er is een bevestigingsmail verstuurd naar ${email}. Bevestig via de link in je inbox om het adres te wijzigen.`,
  firstNameRequired: 'Voornaam is verplicht',
  lastNameRequired: 'Achternaam is verplicht',
  emailRequired: 'E-mailadres is verplicht',
} as const;

// ─── Export ──────────────────────────────────────────────────────────────────
export const EXPORT_LABELS = {
  sectionTitle: 'Exports',
  sectionSubtitle:
    'Download een overzicht van machines per bestelstatus voor het geselecteerde project.',
  machinesFilename: 'machines.xlsx',
  colSupplierEmail: 'E-mail leverancier',
  colStartTime: 'Startuur',
  colEndTime: 'Einduur',
  colNotes: 'Notities',
  colProject: 'Project',
  colAddress: 'Adres',
  exportError: 'Export mislukt. Probeer opnieuw.',
  exportSuccess: 'Export gelukt.',
  bookingsFilename: 'boekingen_per_klant.xlsx',
  colClient: 'Klant',
  colContact: 'Contactpersoon',
  colMachine: 'Machine',
  colSite: 'Werf',
  colStartDate: 'Startdatum',
  colEndDate: 'Einddatum',
  colPickupConfirmed: 'Opgehaald',
  colPickupContact: 'Opgehaald door',
  colHandinConfirmed: 'Teruggebracht',
  colHandinContact: 'Teruggebracht door',
} as const;

// ─── Import ──────────────────────────────────────────────────────────────────
export const IMPORT_LABELS = {
  sectionTitle: 'Import',
  sectionSubtitle:
    'Voeg meerdere machines tegelijk toe via een Excel-bestand voor het geselecteerde project.',
  importButton: 'Importeer xlsx',
  downloadError: 'Template kon niet worden geladen. Probeer opnieuw.',
  downloadSuccess: 'Template gedownload.',
  importError: 'Import mislukt. Probeer opnieuw.',
  templateFilename: 'machines_template.xlsx',
  sheetMachines: 'Machines',
  sheetStatuses: 'Statussen',
  sheetEngineTypes: 'Motortypes',
  sheetSuppliers: 'Leveranciers',
  sheetAddresses: 'Adressen',
  sheetCategories: 'Categorieën',
  sheetOptions: 'Opties',
  sheetSpecifications: 'Specificaties',
  statusesInstruction:
    "Gebruik exact een van de statussen hieronder in de kolom 'Status' van het Machines-tabblad. Een lege of onbekende waarde geeft een foutmelding.",
  engineTypesInstruction:
    "Gebruik exact een van de motortypes hieronder in de kolom 'Motor type' van het Machines-tabblad. Een lege of onbekende waarde geeft een foutmelding.",
  suppliersInstruction:
    "Gebruik een van de leveranciernamen hieronder in de kolom 'Naam leverancier' van het Machines-tabblad. Een onbekende naam geeft een foutmelding.",
  addressesInstruction:
    "Gebruik een van de adresnamen hieronder in de kolom 'Naam adres' van het Machines-tabblad. Een onbekende naam geeft een foutmelding.",
  categoriesInstruction:
    "Dit zijn de gekende categorieën. Vul een van deze namen in bij 'Categorie' van het Machines-tabblad. Een onbekende categorie geeft geen foutmelding - deze wordt als eenmalige vrije tekst bij de machine opgeslagen (niet toegevoegd aan deze lijst) - je krijgt hiervoor wel een waarschuwing.",
  optionsInstruction:
    "Dit zijn de gekende opties / toebehoren. Vul een of meerdere van deze namen in bij 'Opties / toebehoren' van het Machines-tabblad (gescheiden door een komma). Een onbekende optie geeft geen foutmelding - deze wordt als eenmalige vrije tekst bij de machine opgeslagen (niet toegevoegd aan deze lijst) - je krijgt hiervoor wel een waarschuwing.",
  specificationsInstruction:
    "Dit zijn de specificaties die je optioneel per machine kan invullen, met hun eenheid en aantal decimalen. Vul een numerieke waarde in bij de bijhorende kolom van het Machines-tabblad. Een ongeldige waarde geeft geen foutmelding - deze wordt overgeslagen - je krijgt hiervoor wel een waarschuwing.",
  colSpecTitle: 'Specificatie',
  colSpecUnit: 'Eenheid',
  colSpecDecimals: 'Aantal decimalen',
  colName: 'Naam *',
  colCategory: 'Categorie',
  colMachineNumber: 'Machinenummer',
  colStatus: 'Status *',
  colEngineType: 'Motor type *',
  colStartDate: 'Startdatum (DD/MM/YYYY) *',
  colStartTime: 'Starttijd (HH:mm) *',
  colEndDate: 'Einddatum (DD/MM/YYYY) *',
  colEndTime: 'Eindtijd (HH:mm) *',
  colOptions: 'Opties / toebehoren (gescheiden door een komma)',
  colNotes: 'Notities',
  colSupplierName: 'Naam leverancier',
  colAddressName: 'Naam adres',
  colPickupOnEndDate: 'Ophaaldatum bevestigd (ja/nee)',
  pickupYes: 'Ja',
  pickupNo: 'Nee',
  importSuccess: (n: number) => `${n} ${n === 1 ? 'machine' : 'machines'} toegevoegd.`,
  importPartial: (added: number, skipped: number) =>
    `${added} ${added === 1 ? 'machine' : 'machines'} toegevoegd, ${skipped} overgeslagen.`,
  rowErrorNameRequired: (row: number) => `Rij ${row}: naam is verplicht`,
  rowErrorStartDateRequired: (row: number) => `Rij ${row}: startdatum is verplicht`,
  rowErrorEndDateRequired: (row: number) => `Rij ${row}: einddatum is verplicht`,
  rowErrorStartTimeRequired: (row: number) => `Rij ${row}: starttijd is verplicht`,
  rowErrorEndTimeRequired: (row: number) => `Rij ${row}: eindtijd is verplicht`,
  rowErrorInvalidDate: (row: number, field: string) => `Rij ${row}: ongeldige datum in '${field}'`,
  rowErrorInvalidTime: (row: number, field: string) => `Rij ${row}: ongeldige tijd in '${field}'`,
  rowErrorEndBeforeStart: (row: number) => `Rij ${row}: einddatum mag niet voor startdatum liggen`,
  rowErrorEndTimeBeforeStart: (row: number) => `Rij ${row}: eindtijd moet na starttijd liggen`,
  rowErrorInvalidStatus: (row: number) => `Rij ${row}: status is verplicht en moet een van de gekende statussen zijn`,
  rowErrorInvalidEngineType: (row: number) => `Rij ${row}: motor type is verplicht en moet een van de gekende motor types zijn`,
  rowErrorUnknownSupplier: (row: number, name: string) => `Rij ${row}: leverancier '${name}' is niet gekend`,
  rowErrorUnknownAddress: (row: number, name: string) => `Rij ${row}: adres '${name}' is niet gekend`,
  rowErrorInvalidPickup: (row: number) => `Rij ${row}: 'Ophaaldatum bevestigd' moet 'ja' of 'nee' zijn`,
  warningUnknownCategory: (name: string) => `Categorie '${name}' was niet gekend en is als eenmalige vrije tekst opgeslagen (niet toegevoegd aan de lijst).`,
  warningUnknownOption: (name: string) => `Optie '${name}' was niet gekend en is als eenmalige vrije tekst opgeslagen (niet toegevoegd aan de lijst).`,
  warningInvalidSpecification: (row: number, label: string) => `Rij ${row}: ongeldige waarde voor '${label}', overgeslagen.`,
} as const;

// ─── Bulk acties ─────────────────────────────────────────────────────────────
export const BULK_ACTION_LABELS = {
  bulkMode: 'Bulk acties',
  bulkModeExit: 'Klaar',
  bulkBanner: (selected: number, total: number) =>
    `${selected} van ${total} machine${total !== 1 ? 's' : ''} geselecteerd`,
  bulkSelectAll: 'Alles selecteren',
  bulkDeselectAll: 'Alles deselecteren',
  bulkApply: 'Actie uitvoeren',
  bulkNoSelection: 'Selecteer eerst machines om een actie uit te voeren',
  checkboxLabel: (name: string) => `${name} selecteren`,
  // Menu acties
  bulkStatus: 'Status updaten',
  bulkPickupConfirm: 'Ophaaldatum bevestigen',
  bulkPickupUnconfirm: 'Ophaaldatum niet bevestigd',
  bulkSupplier: 'Leverancier updaten',
  bulkStartDate: 'Startdatum updaten',
  bulkEndDate: 'Einddatum updaten',
  // Dialog velden
  bulkStatusLabel: 'Nieuwe status',
  bulkStatusRequired: 'Status is verplicht',
  bulkSupplierLabel: 'Nieuwe leverancier',
  bulkSupplierRequired: 'Leverancier is verplicht',
  bulkStartDateLabel: 'Startdatum',
  bulkStartTimeLabel: 'Starttijd',
  bulkEndDateLabel: 'Einddatum',
  bulkEndTimeLabel: 'Eindtijd',
  bulkTimeRequired: 'Tijdstip is verplicht',
  bulkStartDateRequired: 'Startdatum is verplicht',
  bulkEndDateRequired: 'Einddatum is verplicht',
  // Dialog knoppen
  bulkDialogApply: 'Toepassen',
  bulkDialogCancel: 'Annuleren',
} as const;

export const ADMIN_PAGE_LABELS = {
  admin: 'Admin',
  newOrgTitle: 'Nieuwe organisatie',
  newOrgNameLabel: 'Naam organisatie',
  createButton: 'Aanmaken',
  cancel: 'Annuleren',
  switchOrg: 'Naar deze organisatie',
  orgsTitle: 'Organisaties',
  noOrgs: 'Nog geen organisaties aangemaakt.',
  removeMember: 'Lid verwijderen',
  noEmail: '(geen e-mail)',
  memberCount: (n: number) => `${n} ${n === 1 ? 'lid' : 'leden'}`,
  nameRequired: 'Naam is verplicht',
  createSuccess: 'Organisatie aangemaakt.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  deleteSuccess: 'Lid verwijderd.',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  editLimits: 'Organisatie-instellingen',
  limitsModalTitle: 'Organisatie-instellingen',
  tabLimits: 'Limieten',
  tabSettings: 'Instellingen',
  maxActiveProjectsTitle: 'Actieve projecten',
  maxActiveProjectsDescription: 'Maximum aantal actieve (niet-gearchiveerde) projecten dat deze organisatie mag hebben.',
  maxActiveProjectsLabel: 'Max. actieve projecten',
  unlimitedCheckbox: 'Onbeperkt',
  limitInvalid: 'Vul een geheel getal van 0 of hoger in.',
  limitsSuccess: 'Instellingen opgeslagen.',
  pickupHandinEnabledTitle: 'Ophaling/teruggave bevestiging',
  pickupHandinEnabledDescription:
    'Vereist een expliciete bevestiging van ophaling en teruggave voor elke boeking op een machine, in plaats van de boekingsperiode automatisch als "in gebruik" te beschouwen. Let op: bij het inschakelen tonen alle op dat moment lopende boekingen zich meteen als "Gereserveerd" totdat de ophaling ervoor bevestigd wordt.',
  pickupHandinEnabledSwitchLabel: 'Ingeschakeld',
  save: 'Opslaan',
  lastFullAccessRemoveBlocked: 'Dit is de enige actieve gebruiker met volledige toegang in deze organisatie en kan daarom niet verwijderd worden. Geef eerst iemand anders volledige toegang.',
} as const;

// ─── FAQ ────────────────────────────────────────────────────────────────────
export const FAQ_PAGE_LABELS = {
  title: 'Veelgestelde vragen',
  subtitle: 'Bekijk veelgestelde vragen over Planly, gegroepeerd per categorie. Gebruik de zoekbalk om snel een antwoord te vinden.',
  addLabel: 'Vraag toevoegen',
  searchLabel: 'Zoeken',
  searchPlaceholder: 'Zoek in alle veelgestelde vragen...',
  clearSearch: 'Zoekopdracht wissen',
  categoryFilterLabel: 'Categorie',
  noResults: 'Geen resultaten gevonden.',
  editLabel: 'Vraag bewerken',
  deleteLabel: 'Vraag verwijderen',
  deleteError: 'Verwijderen mislukt. Probeer opnieuw.',
  mutationError: 'Er is een fout opgetreden. Probeer opnieuw.',
  saveSuccess: 'Vraag opgeslagen.',
  deleteSuccess: 'Vraag verwijderd.',
  reorderMode: 'Volgorde aanpassen',
  reorderModeExit: 'Klaar',
  reorderSuccess: 'Volgorde aangepast.',
  reorderError: 'Volgorde kon niet worden opgeslagen. Probeer opnieuw.',
  dragHandle: 'Sleepgreep',
} as const;

export const FAQ_MODAL_LABELS = {
  createTitle: 'Vraag toevoegen',
  editTitle: 'Vraag bewerken',
  categoryLabel: 'Categorie',
  questionLabel: 'Vraag',
  answerLabel: 'Antwoord',
  categoryRequired: 'Kies een categorie.',
  questionRequired: 'Vraag is verplicht.',
  answerRequired: 'Antwoord is verplicht.',
  cancel: 'Annuleren',
  save: 'Opslaan',
} as const;

// ─── Landing ─────────────────────────────────────────────────────────────────
export const LANDING_LABELS = {
  navLogin: 'Inloggen',
  navDemoButton: 'Vraag demo aan',
  heroPricingLink: 'Ga naar prijzen',
  themeLightLabel: 'Licht thema',
  themeDarkLabel: 'Donker thema',

  heroTitleMain: 'Slimmer plannen.',
  heroTitleAccent: 'Meer controle.',
  heroSubtitle: 'Planly helpt je materieel, leveringen en onderaannemers efficiënt te beheren en tijd te besparen.',
  heroTrialButton: 'Probeer 2 weken gratis uit',

  heroMockupMachines: ['HF-102', 'GEN-014', 'TT-207', 'DH-045', 'TW-089'] as readonly string[],

  featuresEyebrow: 'Meer mogelijkheden',
  featuresTitleMain: 'En dit is',
  featuresTitleAccent: 'nog maar het begin.',
  featuresSubtitle: 'Ontdek enkele extra functionaliteiten die Planly compleet maken.',
  featureAssetsTitle: 'Assets & categorieën',
  featureAssetsBody: 'Beheer al je machines, materieel en toebehoren met eigen categorieën, opties en specificaties.',
  featureSuppliersTitle: 'Leveranciersbeheer',
  featureSuppliersBody: 'Houd al je leveranciers overzichtelijk bij, met contactgegevens, voorwaarden en gekoppelde assets.',
  featureProjectsTitle: 'Projecten & locaties',
  featureProjectsBody: 'Werk met meerdere projecten, sites en adressen. Zo zie je in één oogopslag waar welk materieel nodig is.',
  featureEmailsTitle: 'Automatische e-mails',
  featureEmailsBody: 'Laat bestel- en afmeldmails automatisch opstellen en versturen naar leveranciers of interne teams.',
  featureConfigTitle: 'Flexibele configuratie',
  featureConfigBody: 'Richt Planly in op jouw manier van werken. Pas velden, statussen en workflows eenvoudig aan.',
  featureUsersTitle: 'Gebruikers & rechten',
  featureUsersBody: 'Laat je team en onderaannemers samenwerken met de juiste toegangsrechten.',
  featureReportsTitle: 'Rapporten & inzichten',
  featureReportsBody: 'Krijg snel inzicht in je bezetting, gebruik, kosten en beschikbaarheid met overzichtelijke rapporten.',
  featureArchiveTitle: 'Archiveren',
  featureArchiveBody: 'Houd je actieve planning overzichtelijk door oude projecten te archiveren, terwijl je alle data veilig bewaart.',
  featureFaqTitle: 'Nog vragen?',
  featureFaqBody: 'Bekijk onze veelgestelde vragen en ontdek alles wat je moet weten over Planly.',
  featureFaqLinkLabel: 'Ga naar de FAQ',

  plannerEyebrow: 'De Slimme Planner',
  plannerTitle: 'Overzichtelijk plannen zonder gedoe',
  plannerBody: 'Plan al je materieel, leveringen en boekingen in één duidelijke Gantt-planning. Zo weet je altijd wat waar nodig is en voorkom je conflicten.',
  plannerBullets: [
    'Visuele planning van je materieel en bijhorende boekingen',
    'Uitgebreide filtermogelijkheden voor een nauwkeurig resultaat',
    'Bulkacties en vooraf ingevulde e-mails voor bestellingen en afmeldingen',
    'Materieel, leveranciers, sites en projecten overzichtelijk beheren',
  ] as readonly string[],

  smartPlannerBadge: 'Onze superster',
  smartPlannerTitle: 'Planly Smart Planner+',
  smartPlannerBody: 'Onze slimme motor die automatisch kansen ontdekt om meerdere aanvragen met één stuk materieel te combineren.',
  smartPlannerBullets: [
    'Vindt aansluitende boekingen',
    'Stelt automatische optimalisaties voor',
    'Bespaart machines, transporten en kosten',
    'Met één klik toepassen in je planning',
  ] as readonly string[],
  smartPlannerMockupRequestTitles: ['Machine 2', 'Machine 10', 'Machine 23'] as readonly string[],
  smartPlannerMockupRequestDates: ['3 – 11 juli', '12 – 22 juli', '24 – 29 juli'] as readonly string[],
  smartPlannerMockupSuggestionLabel: 'Slimme suggestie',
  smartPlannerMockupSuggestionSubtitle: 'Gebruik 1 stuk materieel i.p.v. 3',
  smartPlannerMockupSuggestionRange: '3 – 29 juli (27 dagen)',
  smartPlannerMockupSavingsLabel: 'Geschatte besparing',
  smartPlannerMockupSavingsValue: '€698',
  smartPlannerMockupSavingsCaption: 'door minder materieel, minder transporten en lager huurtarief',

  dashboardDeepDiveEyebrow: 'Dashboard',
  dashboardDeepDiveTitle: 'Altijd de juiste informatie bij de hand',
  dashboardDeepDiveBody: 'Het dashboard geeft je een helder overzicht van wat vandaag aandacht vraagt en waar actie nodig is.',
  dashboardDeepDiveBullets: [
    'Wat moet er vandaag gebeuren?',
    'Leveringen, afhalingen en acties in één overzicht',
    'Inzicht in bezetting, categorieën en leveranciers',
    'Klik door naar de planning met één klik',
  ] as readonly string[],
  dashboardMockupTileValues: ['3', '5', '4', '2'] as readonly string[],
  dashboardMockupTileLabels: ['Te bestellen', 'Aankomend', 'Vertrekkend', 'Actie vereist'] as readonly string[],

  pickupDeepDiveEyebrow: 'Meer controle over je materieel',
  pickupDeepDiveTitle: 'Pickup & Hand-in',
  pickupDeepDiveBody: 'Weet niet alleen welke firma materieel gebruikt, maar ook wie het effectief heeft afgehaald. Registreer elke afhaling eenvoudig door een naam te selecteren.',
  pickupDeepDiveBullets: [
    'Registreer wie welk materieel afhaalt',
    'Minder verloren of zoekgeraakte materieel',
    'Betere traceerbaarheid bij schade en verlies',
  ] as readonly string[],
  pickupMockupSteps: ['Gereserveerd', 'In gebruik', 'Teruggebracht'] as readonly string[],
  pickupMockupPickedUpByLabel: 'Opgehaald door',
  pickupMockupHandedInByLabel: 'Teruggebracht door',
  pickupMockupName: 'Jan Peeters',
  pickupMockupHandedInName: 'Els De Wilde',

  forWhoTitle: 'Voor wie is Planly?',
  forWhoBody: 'Planly is ontwikkeld voor organisaties die veel materieel tijdelijk moeten plannen, verdelen en opvolgen.',
  forWhoItems: [
    'Festivals en evenementenorganisaties',
    'Eventlocaties en productiebedrijven',
    'Tijdelijke projecten en site managers',
  ] as readonly string[],

  pricingTitle: 'Kies het pakket dat bij jou past',
  pricingTrialNote: 'Probeer Planly eerst 2 weken gratis uit, je koopt nog niets',

  howItWorksEyebrow: 'Hoe het werkt',
  howItWorksTitle: 'Van aanvraag tot planning in drie stappen',
  howItWorksSubtitle: 'Geen lange implementatie, je bent sneller aan het plannen dan je denkt.',
  step1Number: '01',
  step1Title: 'Vraag een demo aan',
  step1Body: 'Laten we een babbel inplannen, we tonen je met veel plezier hoe onze app werkt!',
  step2Number: '02',
  step2Title: 'Richt je organisatie in',
  step2Body: 'Je voegt zelf je projecten, sites en materieel toe in Planly. Loop je vast, dan helpen we je graag verder.',
  step3Number: '03',
  step3Title: 'Begin met plannen',
  step3Body: 'Jij en je team plannen meteen boekingen, bestellingen en opvolging.',

  ctaTitle: 'Klaar om slimmer te plannen?',
  ctaSubtitle: 'Ontdek hoe Planly jouw organisatie tijd en kopzorgen kan besparen.',
  ctaRegisterButton: 'Vrijblijvend kennismaken',

  footerFaqLink: 'Meer vragen? Bekijk de veelgestelde vragen',
  footerCopyright: (year: number) => `© ${year} Planly. Alle rechten voorbehouden.`,
} as const;

export const REGISTER_DIALOG_LABELS = {
  dialogTitle: 'Vrijblijvend kennismaken',
  intro: 'Vul je gegevens in en we nemen zo snel mogelijk contact met je op.',
  nameLabel: 'Naam',
  nameRequired: 'Naam is verplicht.',
  emailLabel: 'E-mailadres',
  emailRequired: 'E-mailadres is verplicht.',
  emailInvalid: 'Vul een geldig e-mailadres in.',
  organizationLabel: 'Organisatie',
  organizationRequired: 'Organisatie is verplicht.',
  pricingInterestLabel: 'Interesse in pakket',
  pricingInterestUnknown: 'Weet ik nog niet',
  messageLabel: 'Toelichting (optioneel)',
  cancel: 'Annuleren',
  submitButton: 'Aanvraag versturen',
  mailSubject: 'Vrijblijvende kennismaking - Planly',
  mailNameLine: 'Naam',
  mailEmailLine: 'E-mailadres',
  mailOrganizationLine: 'Organisatie',
  mailInterestLine: 'Interesse in pakket',
} as const;
